import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, matchPath, useLocation, useNavigate } from 'react-router';
import moment from 'moment';
import ConfirmModal from '../../components/confirm-modal/ConfirmModal';
import { trackEvent } from '../../support-and-tracking/analytics-actions';
import {
    canPublishProject,
    getProjectType,
    hasErrorsInUserForProjectPublish,
    isProjectVersionReadyForPublish,
    isStartingInTheFutureNotPublished,
    projectHasRewardDataTerms,
} from '../project-model';
import ButtonText from '../../components/button/button-text/ButtonText';
import Chevron from '../../components/chevron/Chevron';
import { Subtitle } from '../../components/typography/Heading';
import List from '../../components/text/List';
import VisibilitySensor from '../../components/visibility-sensor/VisibilitySensor';
import styles from './publish-button.module.less';
import { getInvitationFromProjectId } from '../project-invitation-reducer';
import { useUser } from '../../user/useUser';
import { capitalize } from '../../utils';
import { useFeature } from '../../components/hooks/isFeatureEnabled';
import { VERIFY_COLLECTOR_IDENTITY } from '../../constants/features/constants';
import { getCustomRewardsForProject } from '../projects-reducer';
import { PUBLSHING_PROJECT_STOPPED } from '../../constants';
import { ActionButton } from '@sb1/ffe-buttons-react';
import LoginOrRegisterModal from '../../user/login-and-register-modal/LoginOrRegisterModal';
import {
    getProjectStartPath,
    PUBLISH_TRIGGERS,
    START_ORIGIN_PARAM,
    START_ORIGINS,
    usePublishProject,
} from './usePublishProject';

const EDIT_PATH = '/project/:projectId/edit';
const PublishButton = ({
    className = '',
    disabled = false,
    project = null,
    showTips = false,
    school,
    extraText,
    hideWhenNotValid = false,
    alwaysEnabled = false,
    showExtraFirst,
    ariaDescribedby,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const user = useUser();
    const projectInvitation = useSelector((state) => getInvitationFromProjectId(state, project?.id));
    const isPublishing = useSelector((state) => state.projects.isPublishing);
    const [showTip, setShowTip] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const myRef = useRef(null);
    const confirmModalRef = useRef(null);
    const isPublishRequestPending = useRef(false);
    const rewards = useSelector((state) => getCustomRewardsForProject(state, project?.id));
    const showTermsForRewardAddresses = projectHasRewardDataTerms({ project, rewards });
    const executeScroll = () => {
        window ? window.scrollTo(0, myRef.current.offsetTop) : () => {};
    };
    const showVerifyUser = useFeature(VERIFY_COLLECTOR_IDENTITY);
    const publish = usePublishProject();

    if (!project) return null;
    const userErrors = hasErrorsInUserForProjectPublish({
        user,
        project,
        verifyUserIdentity: showVerifyUser,
    });

    const publishProject = (trigger) => {
        if (isPublishRequestPending.current) {
            return;
        }
        isPublishRequestPending.current = true;
        publish({
            project,
            trigger,
            startOrigin: new URLSearchParams(location.search).get(START_ORIGIN_PARAM) || undefined,
        })
            .catch(() => {})
            .finally(() => {
                isPublishRequestPending.current = false;
                confirmModalRef?.current?.close();
            });
    };

    const startPublish = () => {
        if (isPublishing || isPublishRequestPending.current) {
            return;
        }
        if (
            !canPublishProject({
                user,
                project,
                invitation: projectInvitation,
                school,
            })
        ) {
            dispatch({ type: PUBLSHING_PROJECT_STOPPED });
            return;
        }
        if (userErrors.user_not_logged_in) {
            trackEvent('project_publish', 'login-required', { 'project-id': project.id });
            setShowLoginModal(true);
            return;
        }
        if ((hasUserErrors || showTermsForRewardAddresses) && matchPath(EDIT_PATH, location.pathname)) {
            if (hasUserErrors) {
                dispatch({ type: PUBLSHING_PROJECT_STOPPED });
                trackEvent('project_publish', 'missing-user-info', {
                    'project-id': project.id,
                    'missing-user-info': Object.keys(userErrors).join(','),
                });
            }
            navigate(getProjectStartPath({ projectId: project.id, startOrigin: START_ORIGINS.MISSING_USER_INFO }), {
                replace: true,
            });
            return;
        }
        if (isStartingInTheFutureNotPublished(project)) {
            confirmModalRef?.current?.open();
            return;
        }
        publishProject(PUBLISH_TRIGGERS.BUTTON);
    };

    const getButtonText = () => {
        return isPublishing ? 'Starter …' : `Start ${projectType}en`;
    };

    const getConfirmModalMessage = () => {
        if (isStartingInTheFutureNotPublished(project)) {
            return `${capitalize(projectType)}en vil være åpen for innbetalinger fra ${moment(
                project.profile?.publishedAtOverride
            ).format('LLLL')}`;
        }
        return `Er du sikker på at du vil starte ${projectType}en?`;
    };

    const projectType = getProjectType(project);
    const buttonText = getButtonText(project);

    if (!project) {
        return (
            <ActionButton disabled className={className}>
                {buttonText}
            </ActionButton>
        );
    }

    const hasUserErrors = Object.keys(userErrors).length > 0;

    const canPublish = isProjectVersionReadyForPublish({
        projectVersion: project,
        user,
        projectInvitation,
        school,
        verifyUserIdentity: showVerifyUser,
    });

    if (hideWhenNotValid && ((!canPublish && !alwaysEnabled) || showTermsForRewardAddresses)) {
        return null;
    }
    return (
        <>
            {showExtraFirst && extraText && <div className={styles.extraText}>{extraText}</div>}
            <ActionButton
                disabled={(!canPublish && !alwaysEnabled) || disabled || isPublishing}
                className={className}
                onClick={() => {
                    trackEvent('project_publish', 'start');
                    startPublish();
                }}
                aria-describedBy={ariaDescribedby}
            >
                {buttonText}
            </ActionButton>

            {!showExtraFirst && extraText && <div className={styles.extraText}>{extraText}</div>}
            {showTips && (
                <ButtonText
                    onClick={() => {
                        trackEvent('tip', '10-12-before-publish', { projectId: project.id, openTip: !showTip });
                        setShowTip(!showTip);
                    }}
                    className={styles.tipButton}
                >
                    Tips for å lykkes med spleisen ✨
                    <Chevron isOpen={showTip} className={styles.chevron} />
                </ButtonText>
            )}
            {!!showTip && (
                <div className={styles.tipContainer}>
                    <Subtitle tag="h2" className={styles.tipMainTitle}>
                        Engasjer folk tidlig 💡
                    </Subtitle>
                    <p>Skaff 10 givere de første 12 timene! Da øker du sannsynligheten for å lykkes 🙌</p>
                    <List className={styles.tipList}>
                        <li>Når en spleis først har fått litt fart er folk mer villige til å støtte</li>
                        <li>Engasjer venner, familie og nettverket ditt, og få de til å dele spleisen videre</li>
                        <li>Del spleisen på sosiale medier, e-post, SMS og alle andre steder du kan nå folk</li>
                    </List>
                    <p className={styles.tipLink}>
                        <Link to="/tips" target="_blank" ref={myRef}>
                            Se flere gode tips
                        </Link>
                    </p>
                    <VisibilitySensor onVisible={executeScroll} offset={{ bottom: 1000 }} />
                </div>
            )}

            <ConfirmModal
                modalRef={confirmModalRef}
                title={`Alt er klart for at ${projectType}en kan starte`}
                message={getConfirmModalMessage()}
                successText={buttonText}
                cancelText="Avbryt"
                onClickSuccess={() => publishProject(PUBLISH_TRIGGERS.CONFIRM_DIALOG)}
                onClickCancel={() => confirmModalRef?.current?.close()}
                useActionButton={true}
            />
            <LoginOrRegisterModal
                isOpen={showLoginModal}
                onClose={() => setShowLoginModal(false)}
                onHumanClose={() => setShowLoginModal(false)}
                onComplete={() => setShowLoginModal(false)}
                redirectLink={getProjectStartPath({ projectId: project.id, startOrigin: START_ORIGINS.LOGIN })}
                title={`Logg inn eller registrer deg for å starte ${projectType}en`}
            />
        </>
    );
};

export default PublishButton;
